import React, { useState, useEffect, useRef } from 'react';
import RecycleOfferCard from './RecycleOfferCard';

const RecycleOffersSlider = ({ offers, loading }) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const sliderTrackRef = useRef(null);
    const prevBtnRef = useRef(null);
    const nextBtnRef = useRef(null);

    /* Clamp currentSlide so slider never goes past last slide (e.g. 6 offers = 2 or 3 slides) */
    useEffect(() => {
        if (offers.length === 0) return;
        const viewport = sliderTrackRef.current?.parentElement;
        if (!viewport) return;
        const vw = viewport.offsetWidth;
        const gap = 10;
        let cardsPerSlide = vw < 768 ? 1 : vw <= 1366 ? 2 : 3;
        const totalSlides = Math.max(1, Math.ceil(offers.length / cardsPerSlide));
        setCurrentSlide((prev) => Math.min(prev, totalSlides - 1));
    }, [offers.length]);

    useEffect(() => {
        if (offers.length === 0) return;
        initSlider();
    }, [offers]);

    const initSlider = () => {
        if (!sliderTrackRef.current) return;

        const track = sliderTrackRef.current;
        const viewport = track.parentElement;
        if (!viewport) return;

        const updateSlider = () => {
            const viewportWidth = viewport.offsetWidth;
            const gap = 10;
            let cardsPerSlide = 3; // Default 3 cards

            // Responsive cards per slide (2 at 1024–1366 / 1344x641 so 2 offers visible; 3 on wider)
            if (viewportWidth < 768) {
                cardsPerSlide = 1;
            } else if (viewportWidth <= 1366) {
                cardsPerSlide = 2;
            } else {
                cardsPerSlide = 3;
            }

            const totalCards = offers.length;
            const totalSlides = Math.max(1, Math.ceil(totalCards / cardsPerSlide));
            const cardWidth = (viewportWidth - (gap * (cardsPerSlide - 1))) / cardsPerSlide;
            const slideWidth = viewportWidth;
            const translateX = -currentSlide * slideWidth;

            // Set explicit widths so 2 cards show at 1344x641 (normal size)
            track.style.width = `${totalCards * cardWidth + (totalCards - 1) * gap}px`;
            track.style.setProperty('--slider-card-width', `${cardWidth}px`);
            track.style.setProperty('--slider-gap', `${gap}px`);
            track.style.transform = `translateX(${translateX}px)`;

            // Update button states
            if (prevBtnRef.current) {
                prevBtnRef.current.disabled = currentSlide === 0;
            }
            if (nextBtnRef.current) {
                nextBtnRef.current.disabled = currentSlide >= totalSlides - 1;
            }
        };

        updateSlider();

        // Handle window resize
        const handleResize = () => {
            updateSlider();
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    };

    useEffect(() => {
        initSlider();
    }, [currentSlide, offers]);

    const handlePrev = () => {
        setCurrentSlide((prev) => Math.max(0, prev - 1));
    };

    const handleNext = () => {
        const viewport = sliderTrackRef.current?.parentElement;
        if (!viewport) return;

        const viewportWidth = viewport.offsetWidth;
        let cardsPerSlide = 3;
        if (viewportWidth < 768) {
            cardsPerSlide = 1;
        } else if (viewportWidth <= 1366) {
            cardsPerSlide = 2;
        }

        const totalCards = offers.length;
        const totalSlides = Math.max(1, Math.ceil(totalCards / cardsPerSlide));
        setCurrentSlide((prev) => {
            const next = prev + 1;
            if (next >= totalSlides) return prev;
            return next;
        });
    };

    if (loading) {
        return (
            <div className="recycled-lots-section">
                <div className="lots-slider-container" data-slider-id="recycled-lots">
                    <div className="lots-slider-viewport">
                        <div className="lots-slider-track" id="recycled-lots-track">
                            <div className="lots-loading-state">
                                <i className="fas fa-spinner fa-spin"></i>
                                <p>Loading recycled polymer lots...</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (offers.length === 0) {
        return (
            <div className="recycled-lots-section recycled-lots-section--empty">
                <div className="lots-slider-container lots-slider-container--empty" data-slider-id="recycled-lots">
                    <div className="lots-slider-viewport lots-slider-viewport--empty">
                        <div className="lots-slider-track lots-slider-track--empty" id="recycled-lots-track">
                            <div className="lots-empty-state">
                                <p>No offers at the moment.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="recycled-lots-section">
            <div className="lots-slider-container" data-slider-id="recycled-lots">
                <div className="lots-slider-viewport">
                    <div 
                        className="lots-slider-track" 
                        id="recycled-lots-track"
                        ref={sliderTrackRef}
                    >
                        {offers.map((offer, index) => (
                            <RecycleOfferCard key={offer.id || index} offer={offer} />
                        ))}
                    </div>
                </div>
            </div>

            {/* Slider Controls Below Offers */}
            <div className="lots-slider-controls-bottom">
                <button 
                    className="slider-nav prev" 
                    data-slider-control="recycled-lots" 
                    data-direction="prev" 
                    aria-label="Previous offers"
                    onClick={handlePrev}
                    ref={prevBtnRef}
                >
                    <i className="fas fa-chevron-left"></i>
                </button>
                <button 
                    className="slider-nav next" 
                    data-slider-control="recycled-lots" 
                    data-direction="next" 
                    aria-label="Next offers"
                    onClick={handleNext}
                    ref={nextBtnRef}
                >
                    <i className="fas fa-chevron-right"></i>
                </button>
            </div>
        </div>
    );
};

export default RecycleOffersSlider;
